import { useEffect, useRef, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import api from '../../api/axios';
import BrandIcon from '../../widgets/components/BrandIcon';

interface Brand {
  _id: string;
  name: string;
  logoUrl?: string | null;
}

const CarRegistryPage = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [newBrand, setNewBrand] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const [logoBrand, setLogoBrand] = useState<Brand | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchBrands = async () => {
    try {
      const response = await api.get<Brand[]>('/api/admin/brands-car');
      setBrands(response.data);
    } catch (err) {
      setError('Ошибка при загрузке каталога марок.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addBrand = async () => {
    if (!newBrand.trim()) return;
  
    try {
      await api.post<Brand>('/api/admin/brands-car', {
        name: newBrand.trim(),
      });
  
      setNewBrand('');
      await fetchBrands();
    } catch (err: any) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Ошибка при добавлении марки.');
      }
    }
  };
  

  const deleteBrand = async (id: string) => {
    const confirmed = window.confirm(
      'Вы уверены, что хотите удалить эту марку и все её модели?',
    );
    if (!confirmed) return;

    try {
      await api.delete(`/api/admin/brands-car/${id}`);
      setBrands(prev => prev.filter(b => b._id !== id));
    } catch (err) {
      setError('Ошибка при удалении марки.');
      console.error(err);
    }
  };

  const handleRowClick = (brandId: string) => {
    navigate(`/car/${brandId}`);
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const handleClickUpload = (brand: Brand) => {
    setLogoBrand(brand);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !logoBrand) return;
  
    const formData = new FormData();
    formData.append('logo', file);
  
    try {
      const res = await api.post<Brand>(
        `/api/admin/brands-car/${logoBrand._id}/logo`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      );
  
      const logoUrlWithCache = res.data.logoUrl
        ? `${res.data.logoUrl}${res.data.logoUrl.includes('?') ? '&' : '?'}v=${Date.now()}`
        : res.data.logoUrl;
  
      const updatedBrand: Brand = {
        ...res.data,
        logoUrl: logoUrlWithCache,
      };
  
      setBrands(prev =>
        prev.map(b => (b._id === logoBrand._id ? updatedBrand : b)),
      );
    } catch (err) {
      console.error(err);
      setError('Ошибка при загрузке логотипа.');
    } finally {
      e.target.value = '';
      setLogoBrand(null);
    }
  };
  

  return (
    <Box p={4}>
      <Typography variant="h5" gutterBottom>
        Реестр марок автомобилей
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={2} mb={3}>
        <TextField
          label="Новая марка"
          value={newBrand}
          onChange={e => setNewBrand(e.target.value)}
        />
        <Button variant="contained" onClick={addBrand}>
          Добавить
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : brands.length === 0 ? (
        <Typography>Список марок пуст.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>№</TableCell>
                <TableCell>Марка</TableCell>
                <TableCell>Логотип</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {brands.map((brand, index) => (
                <TableRow key={brand._id} hover>
                  <TableCell>{index + 1}</TableCell>

                  <TableCell
                    onClick={() => handleRowClick(brand._id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {brand.name}
                  </TableCell>

                  <TableCell>
                    <BrandIcon logoUrl={brand.logoUrl} />
                  </TableCell>

                  <TableCell align="right">

                    <IconButton
                      size="small"
                      onClick={() => handleClickUpload(brand)}
                      sx={{ mr: 1 }}
                      aria-label="Загрузить логотип"
                    >
                      <CloudUploadIcon />
                    </IconButton>

                    <IconButton
                      color="error"
                      onClick={() => deleteBrand(brand._id)}
                      aria-label="Удалить марку"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
};

export default CarRegistryPage;
